import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { CloseIcon } from "../../assets/icons/closeIcon";
import { DropdownIcon } from "../../assets/icons/dropdownIcon";
import {
  StyledErrorMessageWrapper,
  StyledLabel,
  StyledLabelInfoGroup,
  StyledInfoWrapper,
} from "./FormFieldStyles";
import { InfoIcon } from "../../assets/icons/infoIcon";
import { Tooltip } from "./Tooltip";
import { css, styled } from "styled-components";
import { ErrorIcon } from "../../assets/icons/errorIcon";
import { Text } from "../../utils/text";
import {
  Input,
  FormControl,
  SelectContentRow,
  RequiredIndicator,
  SelectIconLabel,
  SelectIcon,
  SelectMenu as DropdownMenu,
  SelectOption,
  SelectRoot as DropdownWrapper,
  SelectTrigger as DropdownButton,
  SelectValue as SelectedValue,
} from "./Form";
import { designSystemVars as vars } from "../tokens";
import { BareIconButton, ButtonBase } from "./Button";
import { MenuSeparator } from "./Layout";

export interface DropdownOption {
  label: string;
  value: string;
  disabled?: boolean;
  tooltip?: string;
  content?: ReactNode;
  group?: string;
}

interface Props {
  label: string;
  data: DropdownOption[];
  value?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  width?: string;
  isRequired?: boolean;
  searchable?: boolean;
  searchMode?: "typeahead" | "input" | "inline";
  allowCustomValue?: boolean;
  customValueMaxLength?: number;
  leadingContent?: ReactNode;
  toggleIcon?: ReactNode;
  tooltip?: string | null;
  disabled?: boolean;
  preserveDisabledAppearance?: boolean;
  optionLayout?: "default" | "fullWidth" | "detailed";
  searchPlaceholder?: string;
  error?: string;
  name?: string;
}

export const DropdownField: React.FC<Props> = ({
  label,
  data,
  value,
  placeholder = "Select",
  onChange,
  width = "225px",
  isRequired = false,
  searchable = false,
  searchMode = "typeahead",
  allowCustomValue = false,
  customValueMaxLength,
  leadingContent,
  toggleIcon,
  tooltip,
  disabled = false,
  preserveDisabledAppearance = false,
  optionLayout = "default",
  searchPlaceholder,
  error,
  name,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollActiveOptionRef = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mouseDownRef = useRef(false);
  const pendingDraftRef = useRef<string | null>(null);
  const hasSearchInput = searchable && searchMode === "input";
  const hasInlineInput = searchable && searchMode === "inline";
  const hasEditableInput = hasSearchInput || hasInlineInput;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const generatedId = useId();
  const controlId = name || generatedId;
  const labelId = `${controlId}-label`;
  const valueId = `${controlId}-value`;
  const menuId = `${controlId}-menu`;
  const errorId = `${controlId}-error`;

  useEffect(() => {
    if (!open) {
      setSearch("");
      setDraft(null);
      pendingDraftRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!hasEditableInput) return;
    if (disabled) {
      setOpen(false);
      if (hasInlineInput) setIsKeyboardFocus(false);
    } else if (open && hasSearchInput)
      searchInputRef.current?.focus({ preventScroll: true });
  }, [disabled, hasEditableInput, hasInlineInput, hasSearchInput, open]);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!open || !scrollActiveOptionRef.current || !menu || !menu.clientHeight)
      return;

    const options = menu.querySelectorAll<HTMLElement>('[role="option"]');
    const option =
      activeIndex >= 0
        ? options[activeIndex]
        : (!search
            ? menu.querySelector<HTMLElement>(
                '[role="option"][aria-selected="true"]',
              )
            : null) || options[0];
    if (!option) return;
    scrollActiveOptionRef.current = false;

    const visibleTop = menu.getBoundingClientRect().top + menu.clientTop;
    const visibleBottom = visibleTop + menu.clientHeight;
    const bounds = option.getBoundingClientRect();
    if (bounds.top < visibleTop) {
      menu.scrollTop += bounds.top - visibleTop;
    } else if (bounds.bottom > visibleBottom) {
      menu.scrollTop += Math.min(
        bounds.bottom - visibleBottom,
        bounds.top - visibleTop,
      );
    }
  }, [activeIndex, open, search, data]);

  const selected = data.find((d) => d.value === value);

  const filterQuery = hasInlineInput ? search.trim() : search;
  const filteredData = searchable
    ? data.filter((d) =>
        d.label.toLowerCase().includes(filterQuery.toLowerCase()),
      )
    : data;

  const customValue = search.trim();
  const canUseCustomValue =
    allowCustomValue &&
    searchable &&
    customValue.length > 0 &&
    (customValueMaxLength === undefined ||
      customValue.length <= customValueMaxLength) &&
    !data.some(
      (option) => option.label.toLowerCase() === customValue.toLowerCase(),
    );
  const selectableOptions: DropdownOption[] = hasInlineInput
    ? filteredData
    : [
        ...filteredData,
        ...(canUseCustomValue
          ? [{ label: `Use "${customValue}"`, value: customValue }]
          : []),
        ...(allowCustomValue && value && !search
          ? [{ label: "Clear selection", value: "" }]
          : []),
      ];

  const showCursor = searchable && !hasEditableInput && isFocused;
  const isDetailed = optionLayout === "detailed";
  const fullWidthOptions = optionLayout !== "default";
  const displayValue =
    !hasEditableInput && searchable && search
      ? search
      : selected
        ? selected.label
        : allowCustomValue && value
          ? value
          : showCursor
            ? searchPlaceholder || ""
            : placeholder;

  const selectedContent =
    fullWidthOptions &&
    selected &&
    !(searchable && !hasEditableInput && search) ? (
      <Tooltip observeOverflow tooltip={selected.label}>
        {selected.content ?? selected.label}
      </Tooltip>
    ) : (
      displayValue
    );

  const closeDropdown = useCallback(
    (restoreFocus = false) => {
      pendingDraftRef.current = null;
      setOpen(false);
      setIsFocused(false);
      if (hasInlineInput) {
        setSearch("");
        setDraft(null);
        setActiveIndex(-1);
      }
      if (restoreFocus) triggerRef.current?.focus();
    },
    [hasInlineInput],
  );

  const commitInlineDraft = useCallback(
    (confirmUnchanged = false) => {
      const pendingDraft = pendingDraftRef.current;
      if (!hasInlineInput || disabled || pendingDraft === null) return false;

      const trimmed = pendingDraft.trim();
      const exactMatch = data.find(
        (option) => option.label.toLowerCase() === trimmed.toLowerCase(),
      );
      let nextValue: string | undefined;
      if (exactMatch) nextValue = exactMatch.value;
      else if (
        allowCustomValue &&
        trimmed.length > 0 &&
        (customValueMaxLength === undefined ||
          trimmed.length <= customValueMaxLength)
      )
        nextValue = trimmed;
      else if (allowCustomValue && pendingDraft === "") nextValue = "";
      if (nextValue === undefined) return false;

      // Blur and outside clicks can both finish the same edit.
      pendingDraftRef.current = null;
      if (confirmUnchanged || nextValue !== (value ?? "")) onChange(nextValue);
      return true;
    },
    [
      hasInlineInput,
      disabled,
      data,
      allowCustomValue,
      customValueMaxLength,
      value,
      onChange,
    ],
  );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        commitInlineDraft();
        closeDropdown();
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [commitInlineDraft, closeDropdown]);

  const openDropdown = () => {
    if (disabled || (!hasEditableInput && selectableOptions.length === 0))
      return;

    scrollActiveOptionRef.current = true;

    const selectedIndex = selectableOptions.findIndex(
      (option) => option.value === value,
    );
    setActiveIndex(
      hasInlineInput ? -1 : selectedIndex >= 0 ? selectedIndex : 0,
    );
    setOpen(true);
    setIsFocused(true);
  };

  const toggleDropdown = () => {
    if (open) {
      setOpen(false);
      setIsFocused(false);
      return;
    }

    openDropdown();
  };

  const confirmValue = (nextValue: string) => {
    if (disabled) return;
    pendingDraftRef.current = null;
    onChange(nextValue);
    if (hasInlineInput) {
      setIsKeyboardFocus(false);
      searchInputRef.current?.blur();
    }
    closeDropdown(hasSearchInput);
  };

  const selectOption = (index: number) => {
    const option = selectableOptions[index];
    if (option && !option.disabled) confirmValue(option.value);
  };

  const moveActiveOption = (direction: 1 | -1) => {
    if (selectableOptions.length === 0) return;
    scrollActiveOptionRef.current = true;
    setActiveIndex((currentIndex) => {
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0) return selectableOptions.length - 1;
      if (nextIndex >= selectableOptions.length) return 0;
      return nextIndex;
    });
  };

  const updateSearch = (nextSearch: string) => {
    scrollActiveOptionRef.current = true;
    setSearch(nextSearch);
    if (hasInlineInput) {
      pendingDraftRef.current = nextSearch;
      setDraft(nextSearch);
    }
    setActiveIndex(hasInlineInput ? -1 : 0);
    setOpen(true);
    setIsFocused(true);
  };

  const optionList = (
    <>
      {filteredData.length === 0 && (hasInlineInput || !canUseCustomValue) && (
        <div
          style={{
            padding: vars.spacing.sm,
            color: vars.color.control.textEmpty,
          }}
        >
          No results found
        </div>
      )}
      {selectableOptions.map((option, index) => (
        <Fragment key={`${index}-${option.value}`}>
          {index > 0 && option.group !== selectableOptions[index - 1].group && (
            <DropdownGroupSeparator role="separator" aria-hidden="true" />
          )}
          <Option
            id={`${menuId}-option-${index}`}
            role="option"
            aria-selected={option.value === value}
            aria-disabled={option.disabled || undefined}
            $active={
              index === activeIndex ||
              (hasInlineInput &&
                draft === null &&
                activeIndex < 0 &&
                option.value === value)
            }
            $fullWidth={hasEditableInput || fullWidthOptions}
            $editable={hasEditableInput}
            $detailed={isDetailed}
            $keyboardActive={isKeyboardFocus && index === activeIndex}
            onMouseEnter={() => {
              scrollActiveOptionRef.current = false;
              if (isDetailed) setIsKeyboardFocus(false);
              setActiveIndex(index);
            }}
            onMouseDown={(e) => {
              if (hasEditableInput) e.preventDefault();
              if (hasInlineInput) setIsKeyboardFocus(false);
            }}
            onClick={(e) => {
              e.stopPropagation();
              selectOption(index);
            }}
          >
            {leadingContent}
            {option.tooltip ? (
              <Tooltip tooltip={option.tooltip}>
                {option.content ?? option.label}
              </Tooltip>
            ) : fullWidthOptions ? (
              <Tooltip observeOverflow tooltip={option.label}>
                {option.content ?? option.label}
              </Tooltip>
            ) : (
              option.label
            )}
          </Option>
        </Fragment>
      ))}
    </>
  );

  return (
    <DropdownWrapper
      ref={ref}
      style={{ width }}
      onBlur={(e) => {
        if (
          hasEditableInput &&
          !ref.current?.contains(e.relatedTarget as Node)
        ) {
          commitInlineDraft();
          closeDropdown();
          if (hasInlineInput) {
            setIsKeyboardFocus(false);
            mouseDownRef.current = false;
          }
        }
      }}
    >
      {hasInlineInput ? (
        <InlineControl
          $open={open}
          $invalid={!!error}
          $disabled={disabled}
          $focusVisible={isKeyboardFocus && !disabled}
          onMouseDown={(event) => {
            if ((event.target as Element).closest("[data-tooltip-trigger]"))
              return;
            mouseDownRef.current = true;
            setIsKeyboardFocus(false);
          }}
          onClick={(event) => {
            if ((event.target as Element).closest("[data-tooltip-trigger]"))
              return;
            if (!disabled) searchInputRef.current?.focus();
          }}
        >
          {tooltip ? (
            <StyledLabelInfoGroup>
              <StyledLabel
                id={labelId}
                htmlFor={controlId}
                haveError={!!error}
                $active={open}
              >
                {label} {isRequired && <RequiredIndicator>*</RequiredIndicator>}
              </StyledLabel>
              <Tooltip
                tooltip={tooltip}
                triggerTabIndex={0}
                triggerAriaLabel={`${label} information`}
                fullWidth={false}
              >
                <StyledInfoWrapper aria-hidden="true">
                  <InfoIcon />
                </StyledInfoWrapper>
              </Tooltip>
            </StyledLabelInfoGroup>
          ) : (
            <StyledLabel
              id={labelId}
              htmlFor={controlId}
              haveError={!!error}
              $active={open}
            >
              {label} {isRequired && <RequiredIndicator>*</RequiredIndicator>}
            </StyledLabel>
          )}
          <InlineContent>
            <Input
              ref={searchInputRef}
              id={controlId}
              data-field={name}
              role="combobox"
              aria-labelledby={labelId}
              aria-autocomplete="list"
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={open ? menuId : undefined}
              aria-activedescendant={
                open && selectableOptions[activeIndex]
                  ? `${menuId}-option-${activeIndex}`
                  : undefined
              }
              aria-describedby={error ? errorId : undefined}
              aria-invalid={!!error}
              aria-required={isRequired}
              autoComplete="off"
              placeholder={placeholder}
              value={
                draft ??
                selected?.label ??
                (allowCustomValue ? value || "" : "")
              }
              maxLength={allowCustomValue ? customValueMaxLength : undefined}
              disabled={disabled}
              onFocus={() => {
                setIsKeyboardFocus(!mouseDownRef.current);
                mouseDownRef.current = false;
                openDropdown();
              }}
              onClick={() => {
                if (!open) openDropdown();
              }}
              onChange={(e) => updateSearch(e.target.value)}
              onKeyDown={(e) => {
                if (disabled || e.nativeEvent.isComposing) return;
                if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
                  setIsKeyboardFocus(true);
                }
                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  if (!open) openDropdown();
                  moveActiveOption(e.key === "ArrowDown" ? 1 : -1);
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  if (!open) {
                    openDropdown();
                    return;
                  }
                  if (activeIndex >= 0) {
                    selectOption(activeIndex);
                    return;
                  }
                  if (draft === null) {
                    closeDropdown();
                    searchInputRef.current?.blur();
                    return;
                  }
                  if (commitInlineDraft(true)) {
                    closeDropdown();
                    setIsKeyboardFocus(false);
                    searchInputRef.current?.blur();
                  }
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  closeDropdown();
                } else if (e.key === "Tab") {
                  commitInlineDraft();
                  closeDropdown();
                }
              }}
            />
          </InlineContent>
          <InlineToggle
            type="button"
            aria-label={`${open ? "Close" : "Open"} ${label} options`}
            aria-controls={open ? menuId : undefined}
            aria-expanded={open}
            disabled={disabled}
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              if (open) {
                commitInlineDraft();
                closeDropdown();
              } else {
                searchInputRef.current?.focus();
                openDropdown();
              }
            }}
          >
            {toggleIcon ?? (open ? <CloseIcon /> : <DropdownIcon />)}
          </InlineToggle>
        </InlineControl>
      ) : (
        <DropdownTrigger
          $preserveDisabledAppearance={preserveDisabledAppearance}
          ref={triggerRef}
          data-field={name}
          role={hasSearchInput ? undefined : "combobox"}
          aria-label={label}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-activedescendant={
            !hasSearchInput && open && selectableOptions[activeIndex]
              ? `${menuId}-option-${activeIndex}`
              : undefined
          }
          aria-labelledby={labelId}
          aria-describedby={error ? errorId : undefined}
          onClick={() => {
            setIsKeyboardFocus(false);
            toggleDropdown();
          }}
          onBlur={() => {
            setIsKeyboardFocus(false);
            setIsFocused(false);
          }}
          tabIndex={disabled ? -1 : 0}
          $open={open}
          $keyboardFocus={isKeyboardFocus}
          disabled={disabled}
          $invalid={!!error}
          onFocus={(e) => {
            if (e.relatedTarget !== null) {
              setIsKeyboardFocus(true);
            }
            setIsFocused(true);
          }}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === "Tab") {
              setOpen(false);
              return;
            }

            if (e.key === "Escape") {
              if (open) e.preventDefault();
              setOpen(false);
              setIsFocused(false);
              return;
            }

            if (
              e.key === " " &&
              searchable &&
              !hasEditableInput &&
              open &&
              search
            ) {
              e.preventDefault();
              updateSearch(`${search} `);
              return;
            }

            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsKeyboardFocus(true);

              if (open) selectOption(activeIndex);
              else openDropdown();
              return;
            }

            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              setIsKeyboardFocus(true);

              if (!open) {
                openDropdown();
              } else if (selectableOptions.length > 0) {
                moveActiveOption(e.key === "ArrowDown" ? 1 : -1);
              }
              return;
            }

            if (!searchable) return;

            if (e.key === "Backspace") {
              e.preventDefault();
              updateSearch(search.slice(0, -1));
              return;
            }

            if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
              updateSearch(search + e.key);
            }
          }}
        >
          <StyledLabel
            as="span"
            id={tooltip ? undefined : labelId}
            aria-hidden={tooltip ? true : undefined}
            haveError={!!error}
            $active={open}
          >
            {tooltip ? "\u00a0" : label}{" "}
            {!tooltip && isRequired && <RequiredIndicator>*</RequiredIndicator>}
          </StyledLabel>

          <DropdownSelectedValue
            $detailed={isDetailed}
            $searchPlaceholder={
              isDetailed && showCursor && !search && !selected
            }
            id={valueId}
            data-placeholder={
              !selected &&
              !(allowCustomValue && value) &&
              (!search || hasEditableInput) &&
              displayValue === placeholder
                ? "true"
                : undefined
            }
            $showCursor={showCursor}
            style={{
              width: fullWidthOptions ? "100%" : undefined,
            }}
          >
            {leadingContent ? (
              <SelectIconLabel as="span">
                {leadingContent}
                <SelectedValue>{selectedContent}</SelectedValue>
              </SelectIconLabel>
            ) : (
              selectedContent
            )}
          </DropdownSelectedValue>

          <DropdownIconWrapper
            onClick={(e) => {
              e.stopPropagation();
              setIsKeyboardFocus(false);
              toggleDropdown();
            }}
            style={{
              cursor: disabled ? "not-allowed" : "default",
              zIndex: 2,
            }}
          >
            {toggleIcon ?? (open ? <CloseIcon /> : <DropdownIcon />)}
          </DropdownIconWrapper>
        </DropdownTrigger>
      )}

      {tooltip && !hasInlineInput && (
        <DropdownHelpLabel>
          <StyledLabel
            as="span"
            id={labelId}
            haveError={!!error}
            $active={open}
          >
            {label} {isRequired && <RequiredIndicator>*</RequiredIndicator>}
          </StyledLabel>
          <Tooltip tooltip={tooltip}>
            <BareIconButton
              aria-label={`About ${label.toLowerCase()} selection`}
            >
              <InfoIcon />
            </BareIconButton>
          </Tooltip>
        </DropdownHelpLabel>
      )}

      {open && (
        <DropdownOptionsMenu
          ref={menuRef}
          $detailed={isDetailed}
          $inline={hasInlineInput}
          id={hasSearchInput ? undefined : menuId}
          role={hasSearchInput ? undefined : "listbox"}
          aria-label={hasSearchInput ? undefined : `${label} options`}
          style={{
            paddingRight: isDetailed ? vars.spacing.sm : vars.spacing.xs,
            paddingBottom: isDetailed ? vars.spacing.sm : vars.spacing.md,
            paddingTop: isDetailed ? vars.spacing.sm : vars.spacing.px5,
            maxHeight: isDetailed ? "300px" : "224px",
            overflowY: "auto",
          }}
        >
          {hasSearchInput ? (
            <>
              <SearchInput
                ref={searchInputRef}
                role="combobox"
                aria-label={`Search ${label}`}
                aria-autocomplete="list"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={menuId}
                aria-activedescendant={
                  selectableOptions[activeIndex]
                    ? `${menuId}-option-${activeIndex}`
                    : undefined
                }
                aria-describedby={error ? errorId : undefined}
                autoComplete="off"
                placeholder={`Search ${label}`}
                value={search}
                maxLength={allowCustomValue ? customValueMaxLength : undefined}
                disabled={disabled}
                onChange={(e) => updateSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (disabled || e.nativeEvent.isComposing) return;
                  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                    e.preventDefault();
                    moveActiveOption(e.key === "ArrowDown" ? 1 : -1);
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    selectOption(activeIndex);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    closeDropdown(true);
                  } else if (e.key === "Tab") {
                    closeDropdown(true);
                  }
                }}
              />
              <InputOptionList
                id={menuId}
                role="listbox"
                aria-label={`${label} options`}
              >
                {optionList}
              </InputOptionList>
            </>
          ) : (
            optionList
          )}
        </DropdownOptionsMenu>
      )}

      {error && (
        <StyledErrorMessageWrapper id={errorId} role="alert">
          <ErrorIcon aria-hidden="true" />
          <Text
            fontFamily={vars.typography.family.sans}
            fontSize="var(--inzeloop-ds-font-size-xs)"
            fontStyle="normal"
            fontWeight="var(--inzeloop-ds-font-weight-regular)"
            $lineHeight="var(--inzeloop-ds-line-height-sm)"
            color={vars.color.control.errorText}
          >
            {error}
          </Text>
        </StyledErrorMessageWrapper>
      )}
    </DropdownWrapper>
  );
};

const Option = styled(SelectOption)<{
  $active: boolean;
  $fullWidth: boolean;
  $editable: boolean;
  $detailed: boolean;
  $keyboardActive: boolean;
}>`
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "147px")};
  min-width: 0;
  box-sizing: ${({ $fullWidth }) => ($fullWidth ? "border-box" : undefined)};
  height: ${({ $detailed, $editable }) =>
    $detailed
      ? `calc(${vars.spacing.xl} + ${vars.spacing.lg})`
      : $editable
        ? "auto"
        : "32px"};
  &[aria-disabled="true"] {
    color: ${vars.color.text.disabled};
    cursor: not-allowed;
  }
  background: ${({ $active }) =>
    $active ? vars.color.background.subtle : vars.color.background.transparent};

  ${({ $detailed, $keyboardActive }) =>
    $detailed &&
    css`
      color: ${vars.color.text.primary};
      font-weight: ${vars.typography.weight.regular};
      line-height: ${vars.typography.lineHeight.lg};
      background: ${vars.color.background.transparent};
      outline: ${$keyboardActive
        ? `${vars.borderWidth.thin} solid ${vars.color.border.accent}`
        : "none"};
      outline-offset: -${vars.borderWidth.thin};
      &:hover {
        background: ${vars.color.border.subtle};
      }
    `}
`;

const DropdownTrigger = styled(DropdownButton)<{
  $preserveDisabledAppearance: boolean;
}>`
  ${({ $preserveDisabledAppearance }) =>
    $preserveDisabledAppearance &&
    css`
      &:disabled {
        background: ${vars.color.control.background};
      }
    `}
`;

const DropdownIconWrapper = styled(SelectIcon)`
  position: absolute;
  top: ${vars.spacing.px18};
  right: ${vars.spacing.sm};
`;

const DropdownSelectedValue = styled(SelectedValue)<{
  $detailed: boolean;
  $searchPlaceholder: boolean;
}>`
  padding-left: ${vars.spacing.none};
  padding-top: ${vars.spacing.xs};

  &[data-placeholder="true"] {
    color: ${vars.color.control.placeholder};

    ${SelectedValue} {
      color: inherit;
    }
  }

  ${({ $detailed }) =>
    $detailed &&
    css`
      color: ${vars.color.text.primary};
      font-weight: ${vars.typography.weight.regular};
      line-height: ${vars.typography.lineHeight.lg};
    `}
  ${({ $searchPlaceholder }) =>
    $searchPlaceholder &&
    css`
      color: ${vars.color.control.placeholder};
      font-size: ${vars.typography.size.md};
      line-height: ${vars.typography.lineHeight.sm};
    `}
`;

const DropdownOptionsMenu = styled(DropdownMenu)<{
  $detailed: boolean;
  $inline: boolean;
}>`
  top: ${({ $inline }) => ($inline ? "100%" : "102%")};
  ${({ $detailed }) =>
    $detailed &&
    css`
      &::-webkit-scrollbar {
        width: ${vars.spacing.xs};
      }
      &::-webkit-scrollbar-thumb {
        background: ${vars.color.control.scrollbar};
        border-radius: ${vars.radius["2xs"]};
      }
    `}
`;

const DropdownGroupSeparator = styled(MenuSeparator)`
  width: 100%;
  flex-shrink: 0;
  height: ${vars.borderWidth.thin};
  background: ${vars.color.border.control};
  margin: ${vars.spacing.sm} 0;
`;

const DropdownHelpLabel = styled(StyledLabelInfoGroup)`
  position: absolute;
  top: calc(${vars.spacing.xxs} + ${vars.borderWidth.thin});
  left: calc(${vars.spacing.md} + ${vars.borderWidth.thin});
  pointer-events: none;

  [data-tooltip-trigger] {
    pointer-events: auto;
  }
`;

const SearchInput = styled(Input)`
  flex-shrink: 0;
  margin-bottom: ${vars.spacing.xs};
  padding: ${vars.spacing.xs} ${vars.spacing.sm};
  border: ${vars.borderWidth.thin} solid ${vars.color.control.border};
  border-radius: ${vars.radius.xs};

  &:focus-visible {
    box-shadow: ${vars.shadow.focus};
  }
`;

const InputOptionList = styled.div`
  width: 100%;
`;

const InlineControl = styled(FormControl)<{ $open: boolean }>`
  position: relative;
  flex: 1;
  gap: ${vars.spacing.none};
  padding: ${vars.spacing.xxs} ${vars.spacing.md};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "text")};

  &:focus-within {
    border-width: ${({ $open, $invalid, $focusVisible }) =>
      $open || $invalid || $focusVisible
        ? vars.borderWidth.medium
        : vars.borderWidth.thin};
    border-color: ${({ $open, $invalid, $focusVisible }) =>
      $invalid
        ? vars.color.control.borderError
        : $open || $focusVisible
          ? vars.color.border.accent
          : vars.color.control.border};
  }

  ${({ $open, $invalid }) =>
    $open &&
    css`
      border-width: ${vars.borderWidth.medium};
      border-color: ${$invalid
        ? vars.color.control.borderError
        : vars.color.border.accent};
      border-bottom: 0;
      border-radius: ${vars.radius.sm} ${vars.radius.sm} ${vars.radius.none}
        ${vars.radius.none};
    `}
`;

const InlineContent = styled(SelectContentRow)`
  padding-right: ${vars.spacing.xl};
  box-sizing: border-box;

  input {
    padding: ${vars.spacing.none};
  }
`;

const InlineToggle = styled(ButtonBase)`
  position: absolute;
  top: ${vars.spacing.px18};
  right: ${vars.spacing.sm};
  display: flex;
  padding: ${vars.spacing.none};
  border: 0;
  background: ${vars.color.background.transparent};
  color: ${vars.color.control.text};

  &:disabled {
    cursor: not-allowed;
  }
`;
